import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Clock } from 'lucide-react';
import { HostSettingsLayout } from '../components/HostSettingsLayout';
import { formatEuropeanDate } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const STATUS_COLORS = {
  PAID: { bg: 'var(--status-success-surface)', text: 'var(--status-success-text)' },
  PENDING_GUEST: { bg: 'var(--status-warning-surface)', text: 'var(--status-warning-text)' },
  CANCELLED: { bg: 'var(--status-danger-surface)', text: 'var(--status-danger-text)' },
  EXPIRED: { bg: 'var(--surface-2)', text: 'var(--text-3)' },
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/transactions/my/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTransactions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const safeNum = (v) => (typeof v === 'number' ? v : Number(v) || 0);

  return (
    <HostSettingsLayout
      title="Payment History"
      dataTestId="guest-payment-history-page"
      onBack={() => navigate('/guest/settings')}
    >
      <p style={{ fontFamily: FONT, fontSize: 14, color: 'var(--text-2)', textAlign: 'center', marginTop: -12, marginBottom: 8 }}>
        Your stays at Allure venues
      </p>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="payment-history-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ height: 80, background: 'var(--surface-2)', borderRadius: 14, opacity: 0.6 }} />
          ))}
        </div>
      ) : transactions.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: 40 }} data-testid="payment-history-empty">
          <Clock size={40} color="#E7E7E7" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontFamily: FONT, fontSize: 15, color: 'var(--text-2)', fontWeight: 600 }}>No transactions yet</p>
          <p style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Visit an Allure venue to make your first stay
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="payment-history-list">
          {transactions.map((txn) => {
            const colors = STATUS_COLORS[txn.status] || STATUS_COLORS.PAID;
            const original = safeNum(txn.original_amount);
            const discounted = safeNum(txn.discounted_amount);
            const savedDelta = original > 0 ? original - discounted : 0;
            return (
              <div
                key={txn.id}
                data-testid={`payment-history-row-${txn.id}`}
                style={{
                  background: 'var(--surface)',
                  borderRadius: 14,
                  padding: 14,
                  boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
                  border: '1px solid #F1F1F1',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--accent-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {txn.status === 'PAID' ? (
                        <TrendingUp size={16} color="#00C853" />
                      ) : (
                        <TrendingDown size={16} color="#999999" />
                      )}
                    </div>
                    <div>
                      <p style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                        {txn.venue_name || 'Venue'}
                      </p>
                      <p style={{ fontFamily: FONT, fontSize: 11, color: 'var(--text-2)', margin: 0 }}>
                        {formatEuropeanDate(txn.created_at)}
                      </p>
                    </div>
                  </div>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      background: colors.bg,
                      color: colors.text,
                      fontFamily: FONT,
                    }}
                  >
                    {String(txn.status || '').replace('_', ' ')}
                  </span>
                </div>

                {txn.status === 'PAID' && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--hairline)', paddingTop: 8, marginTop: 6 }}>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--text-2)' }}>
                      {original > 0 ? (
                        <>
                          <span style={{ textDecoration: 'line-through' }}>{original.toFixed(2)}€</span>
                          <span style={{ margin: '0 6px' }}>→</span>
                        </>
                      ) : null}
                      <span style={{ fontWeight: 700, color: 'var(--text)' }}>{discounted.toFixed(2)}€</span>
                    </div>
                    {savedDelta > 0 ? (
                      <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#00C853' }}>
                        Saved {savedDelta.toFixed(2)}€
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </HostSettingsLayout>
  );
}
